<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    :stroke="color"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <component :is="rendered" />
  </svg>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'

type IconName =
  | 'meal'
  | 'dumbbell'
  | 'reports'
  | 'person'
  | 'people'
  | 'chevron'
  | 'chevronDown'
  | 'chevronUp'
  | 'chevronLeft'
  | 'plus'
  | 'minus'
  | 'check'
  | 'close'
  | 'edit'
  | 'moon'
  | 'sun'
  | 'beach'
  | 'globe'
  | 'logout'
  | 'clock'
  | 'calendar'
  | 'copy'
  | 'search'
  | 'userPlus'
  | 'arrowUp'
  | 'drag'
  | 'sparkle'
  | 'filter'
  | 'trash'
  | 'archive'
  | 'unarchive'
  | 'eye'
  | 'send'
  | 'visibility'
  | 'menu'
  | 'bell'
  | 'help'

const props = withDefaults(
  defineProps<{
    name: string
    size?: number
    color?: string
    strokeWidth?: number
  }>(),
  {
    size: 18,
    color: 'currentColor',
    strokeWidth: 1.8,
  },
)

const paths: Record<IconName, () => unknown> = {
  meal: () =>
    h('g', null, [
      h('path', { d: 'M4 9h10a3 3 0 0 1 0 6H4z' }),
      h('path', { d: 'M14 11h3a2 2 0 1 1 0 4h-3' }),
      h('path', { d: 'M4 18h10' }),
      h('path', { d: 'M6 6c0-.8.4-1.5 1-2 .6.5 1 1.2 1 2s-.4 1.5-1 2c-.6-.5-1-1.2-1-2z' }),
    ]),
  dumbbell: () =>
    h('g', null, [
      h('path', { d: 'm6.5 6.5 11 11' }),
      h('path', { d: 'm21 21-1-1' }),
      h('path', { d: 'm3 3 1 1' }),
      h('path', { d: 'm18 22 4-4' }),
      h('path', { d: 'm2 6 4-4' }),
      h('path', { d: 'm3 10 7-7' }),
      h('path', { d: 'm14 21 7-7' }),
    ]),
  reports: () =>
    h('g', null, [
      h('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
      h('path', { d: 'M8 14v3' }),
      h('path', { d: 'M12 10v7' }),
      h('path', { d: 'M16 7v10' }),
    ]),
  person: () => h('g', null, [h('circle', { cx: 12, cy: 8, r: 4 }), h('path', { d: 'M4 21a8 8 0 0 1 16 0' })]),
  people: () =>
    h('g', null, [
      h('circle', { cx: 9, cy: 8, r: 3.5 }),
      h('path', { d: 'M2 20a7 7 0 0 1 14 0' }),
      h('circle', { cx: 17, cy: 9, r: 3 }),
      h('path', { d: 'M15 14a6 6 0 0 1 7 6' }),
    ]),
  chevron: () => h('path', { d: 'm9 6 6 6-6 6' }),
  chevronDown: () => h('path', { d: 'm6 9 6 6 6-6' }),
  chevronUp: () => h('path', { d: 'm6 15 6-6 6 6' }),
  chevronLeft: () => h('path', { d: 'm15 6-6 6 6 6' }),
  plus: () => h('g', null, [h('path', { d: 'M12 5v14' }), h('path', { d: 'M5 12h14' })]),
  minus: () => h('path', { d: 'M5 12h14' }),
  check: () => h('path', { d: 'm5 12 5 5 9-12' }),
  close: () => h('g', null, [h('path', { d: 'M6 6l12 12' }), h('path', { d: 'M18 6 6 18' })]),
  edit: () =>
    h('g', null, [
      h('path', { d: 'M12 20h9' }),
      h('path', { d: 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' }),
    ]),
  moon: () => h('path', { d: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z' }),
  sun: () =>
    h('g', null, [
      h('circle', { cx: 12, cy: 12, r: 4 }),
      h('path', { d: 'M12 3v2' }),
      h('path', { d: 'M12 19v2' }),
      h('path', { d: 'm4.9 4.9 1.4 1.4' }),
      h('path', { d: 'm17.7 17.7 1.4 1.4' }),
      h('path', { d: 'M3 12h2' }),
      h('path', { d: 'M19 12h2' }),
      h('path', { d: 'm4.9 19.1 1.4-1.4' }),
      h('path', { d: 'm17.7 6.3 1.4-1.4' }),
    ]),
  // Beach parasol, used for vacation. Deliberately distinct from `sun`, which the
  // light-theme control owns: a straight pole (not a rain umbrella's J-hook) and a
  // scalloped hem keep the two readable side by side at toggle size.
  beach: () =>
    h('g', null, [
      h('path', { d: 'M2.5 12.5a9.5 9.5 0 0 1 19 0' }),
      h('path', { d: 'M2.5 12.5q2.375-1.8 4.75 0 2.375-1.8 4.75 0 2.375-1.8 4.75 0 2.375-1.8 4.75 0' }),
      h('path', { d: 'M12 12.5V21' }),
    ]),
  globe: () =>
    h('g', null, [
      h('circle', { cx: 12, cy: 12, r: 9 }),
      h('path', { d: 'M3 12h18' }),
      h('path', { d: 'M12 3a13 13 0 0 1 0 18' }),
      h('path', { d: 'M12 3a13 13 0 0 0 0 18' }),
    ]),
  logout: () =>
    h('g', null, [
      h('path', { d: 'M15 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4' }),
      h('path', { d: 'm10 17 5-5-5-5' }),
      h('path', { d: 'M15 12H3' }),
    ]),
  clock: () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 9 }), h('path', { d: 'M12 7v5l3 2' })]),
  calendar: () =>
    h('g', null, [
      h('rect', { x: 3, y: 5, width: 18, height: 16, rx: 2 }),
      h('path', { d: 'M8 3v4' }),
      h('path', { d: 'M16 3v4' }),
      h('path', { d: 'M3 10h18' }),
    ]),
  copy: () =>
    h('g', null, [
      h('rect', { x: 8, y: 8, width: 12, height: 12, rx: 2 }),
      h('path', { d: 'M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3' }),
    ]),
  search: () => h('g', null, [h('circle', { cx: 11, cy: 11, r: 7 }), h('path', { d: 'm21 21-4.3-4.3' })]),
  userPlus: () =>
    h('g', null, [
      h('circle', { cx: 9, cy: 8, r: 4 }),
      h('path', { d: 'M2 21a7 7 0 0 1 14 0' }),
      h('path', { d: 'M19 8v6' }),
      h('path', { d: 'M16 11h6' }),
    ]),
  arrowUp: () => h('g', null, [h('path', { d: 'M12 19V5' }), h('path', { d: 'm5 12 7-7 7 7' })]),
  drag: () =>
    h('g', { fill: 'currentColor', stroke: 'none' }, [
      h('circle', { cx: 9, cy: 7, r: 1 }),
      h('circle', { cx: 15, cy: 7, r: 1 }),
      h('circle', { cx: 9, cy: 12, r: 1 }),
      h('circle', { cx: 15, cy: 12, r: 1 }),
      h('circle', { cx: 9, cy: 17, r: 1 }),
      h('circle', { cx: 15, cy: 17, r: 1 }),
    ]),
  sparkle: () =>
    h('g', null, [
      h('path', { d: 'M12 3v4' }),
      h('path', { d: 'M12 17v4' }),
      h('path', { d: 'M3 12h4' }),
      h('path', { d: 'M17 12h4' }),
      h('path', { d: 'm5 5 2.5 2.5' }),
      h('path', { d: 'm16.5 16.5 2.5 2.5' }),
      h('path', { d: 'm5 19 2.5-2.5' }),
      h('path', { d: 'm16.5 7.5 2.5-2.5' }),
    ]),
  filter: () => h('path', { d: 'M4 5h16l-6 8v6l-4-2v-4z' }),
  trash: () =>
    h('g', null, [
      h('path', { d: 'M3 6h18' }),
      h('path', { d: 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }),
      h('path', { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' }),
      h('path', { d: 'M10 11v6' }),
      h('path', { d: 'M14 11v6' }),
    ]),
  archive: () =>
    h('g', null, [
      h('rect', { x: 3, y: 3, width: 18, height: 5, rx: 1 }),
      h('path', { d: 'M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8' }),
      h('path', { d: 'M10 12h4' }),
    ]),
  unarchive: () =>
    h('g', null, [
      h('rect', { x: 3, y: 3, width: 18, height: 5, rx: 1 }),
      h('path', { d: 'M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8' }),
      h('path', { d: 'M12 18v-6' }),
      h('path', { d: 'm9 15 3-3 3 3' }),
    ]),
  eye: () =>
    h('g', null, [
      h('path', { d: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z' }),
      h('circle', { cx: 12, cy: 12, r: 3 }),
    ]),
  send: () => h('g', null, [h('path', { d: 'M22 2 11 13' }), h('path', { d: 'M22 2 15 22l-4-9-9-4 20-7z' })]),
  visibility: () =>
    h('g', null, [
      h('path', { d: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z' }),
      h('circle', { cx: 12, cy: 12, r: 3 }),
    ]),
  menu: () => h('g', null, [h('path', { d: 'M3 6h18' }), h('path', { d: 'M3 12h18' }), h('path', { d: 'M3 18h18' })]),
  bell: () =>
    h('g', null, [
      h('path', { d: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9' }),
      h('path', { d: 'M10.3 21a1.94 1.94 0 0 0 3.4 0' }),
    ]),
  help: () =>
    h('g', null, [
      h('circle', { cx: 12, cy: 12, r: 10 }),
      h('path', { d: 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' }),
      h('path', { d: 'M12 17h.01' }),
    ]),
}

const rendered = computed(() => {
  const fn = (paths as Record<string, () => unknown>)[props.name]
  return fn ? fn() : null
})
</script>
